import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api, money } from "@/lib/api";
import { useStore } from "@/store/StoreContext";
import { Eyebrow } from "@/components/Reveal";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useStore();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) {
            setState("paid");
            clearCart();
            api.get(`/orders/by-session/${sessionId}`).then((r) => setOrder(r.data)).catch(() => {});
          }
          return;
        }
      } catch {}
      attempts += 1;
      if (!cancelled) {
        if (attempts < 12) setTimeout(poll, 2000);
        else setState("pending");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  const orderId = sessionId ? `JN-${sessionId.slice(-8).toUpperCase()}` : null;

  return (
    <div className="pt-16 min-h-[80vh] flex items-center" data-testid="payment-success-page">
      <div className="max-w-xl mx-auto px-4 text-center py-24 w-full">
        {state === "checking" && (
          <>
            <Loader2 size={32} className="text-gold animate-spin mx-auto mb-8" />
            <Eyebrow className="justify-center">Confirming</Eyebrow>
            <h1 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-4" data-testid="payment-status-title">Sealing the Bag</h1>
            <p className="text-cream/50 text-sm mt-6">Confirming your payment with Stripe…</p>
          </>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="w-16 h-16 mx-auto mb-8 border border-gold flex items-center justify-center">
              <Check size={28} className="text-gold" />
            </div>
            <Eyebrow className="justify-center">Order Confirmed</Eyebrow>
            <h1 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-4" data-testid="payment-status-title">Yours.</h1>
            <p className="font-mono2 text-gold text-sm mt-6 tracking-[0.2em]" data-testid="order-id">{orderId}</p>

            {order && (
              <div className="mt-8 border border-white/10 p-6 text-left max-w-sm mx-auto" data-testid="order-summary">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between gap-4 text-sm text-cream/70 py-2 border-b border-white/5 last:border-0">
                    <span>{i.qty} × {i.name} — Size {i.size}</span>
                    <span className="font-mono2 text-gold whitespace-nowrap">{money(i.price * i.qty)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-3 text-sm">
                  <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-cream/40">Total</span>
                  <span className="font-mono2 text-gold">{money(order.amount)}</span>
                </div>
                {order.email && (
                  <p className="text-xs text-cream/40 mt-4">Confirmation sent to {order.email}. Save your order number — that's your receipt.</p>
                )}
              </div>
            )}

            <p className="text-cream/50 text-sm mt-6 max-w-sm mx-auto leading-relaxed">
              Your pieces are being prepared in numbered packaging. Sign in to your account anytime to see this order.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link to="/account" className="border border-cream/30 px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors" data-testid="success-account-link">View Orders</Link>
              <Link to="/shop" className="bg-gold text-noir-bg px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors" data-testid="success-shop-link">Keep Shopping</Link>
            </div>
          </motion.div>
        )}
        {state === "pending" && (
          <>
            <Eyebrow className="justify-center">Processing</Eyebrow>
            <h1 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-4" data-testid="payment-status-title">Almost There</h1>
            <p className="text-cream/50 text-sm mt-6 max-w-sm mx-auto">
              Your payment is still processing. It will appear in your account orders shortly.
            </p>
            <Link to="/account" className="inline-block mt-10 border border-cream/30 px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors" data-testid="success-account-link">Go to Account</Link>
          </>
        )}
        {state === "error" && (
          <>
            <Eyebrow className="justify-center">Something's Off</Eyebrow>
            <h1 className="font-display font-extrabold uppercase text-3xl sm:text-5xl mt-4" data-testid="payment-status-title">No Session</h1>
            <p className="text-cream/50 text-sm mt-6">We couldn't find that checkout session.</p>
            <Link to="/shop" className="inline-block mt-10 bg-gold text-noir-bg px-8 py-4 font-mono2 text-[10px] tracking-[0.25em] uppercase hover:bg-cream transition-colors" data-testid="success-shop-link">Back to Shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
